<script>
    import Icon from '$component/Icon.svelte';

    import { twMerge } from "tailwind-merge";
    import { Select, SuccessIcon } from "m7-library";
    import { t, plural } from "$utils/lang.js";
    import { currencyFormatter } from "$utils/formatter.js";
    import { updateItemQuantity, deleteItem } from "$lib/basketStore.svelte.js";

    /**
     * @type {{
     *   item: import('$lib/basketStore.svelte.js').BasketItem ,
     *   class: string,
     *   edit: boolean,
     *   showDeliveryStatus: boolean
     * }}
     */
    const {
        /** @type BasketItem */
        item,
        class: cls = "", // Renamed to avoid conflict with reserved keyword 'class'
        edit = true,
        showDeliveryStatus = true,
    } = $props();

    let quantity = $derived(item.quantity); // Local state for the select component
    let hasChanged = $state(false); // "Dirty" flag to track user changes

    // Generate options for the Select component based on item.max
    const options = $derived(() => {
        const maxQuantity = item.max || 0; // Ensure a minimum of 1 if max is not set or 0
        const opts = [];
        for (let i = 0; i <= maxQuantity; i++) {
            opts.push({ value: i, label: i.toString() });
        }
        return opts;
    });

    const nameForDisplay = $derived(item.nameParts.join("<br>"));

    // This effect only runs when `hasChanged` becomes true.
    $effect(() => {
        if (hasChanged) {
            updateItemQuantity(item.id, quantity);
            hasChanged = false; // Reset the flag after updating
        }
    });
</script>

<div class={twMerge("flex items-start gap-4 p-3 bg-white text-sm", cls)} data-basket-item={item.id} data-basket-qty={quantity} data-basket-total={item.lineItemTotal} data-product-root-category={item.mainRootCategory || undefined} data-product-category={item.mainCategory || undefined}>
    <div
        class="block overflow-hidden basis-22 shrink-0 border border-grey rounded-md p-2"
        class:w-22={edit}
        class:w-15={!edit}
        class:h-22={edit}
        class:h-15={!edit}
        class:basis-22={edit}
        class:basis-15={!edit}
    >
        <picture>
            {#if item.image && item.image.sources}
                {#each item.image.sources as source}
                    <source srcset={source.srcset} type={source.type} />
                {/each}
            {/if}
            {#if item.image && item.image.default}
                <img
                    src={item.image.default.src}
                    alt={item.name}
                    width={item.image.default.width}
                    height={item.image.default.height}
                    class="object-scale-down w-full h-full"
                />
            {/if}
        </picture>
    </div>
    <div class="flex-grow text-xsm">
        <div class="flex justify-between gap-1 items-end">
            <p
                class:text-xsm={!edit}
                class:text-xs={edit}
                class="font-light uppercase text-dark-gray tracking-widest"
            >
                {item.brand}
            </p>
            <p class="font-bold">
                {currencyFormatter.format(item.lineItemTotal)}
            </p>
        </div>
        <div class="flex flex-col gap-2 items-start">
            {#if edit}
                <a href={item.link} class="font-bold text-xsm text-cyprus"
                    >{@html nameForDisplay}</a
                >
            {:else}
                <span class="font-bold">{@html nameForDisplay}</span>
            {/if}
            <!-- Gewaehlte Aufpreis-Optionen (m7_upgrade_article). reference: [1] -->
            {#if item.persParamInfo?.length}
                {#each item.persParamInfo as zeile}
                    <span class="text-xs text-dark-gray">{zeile}</span>
                {/each}
            {/if}
            {#if showDeliveryStatus}
                <p class="flex gap-1 text-xs font-black tracking-widest">
                    <SuccessIcon class="h-4 w-4" />
                    <!-- reference: [basketStore.svelte 3] -->
                    <span>{item.deliveryDays === 0
                        ? t('Sofort Versandbereit')
                        : item.deliveryDays > 0
                            ? t(plural(item.deliveryDays, {one: 'Versandbereit in {days} Tag', other: 'Versandbereit in {days} Tagen'}), {days: item.deliveryDays})
                            : t(item.deliveryStatus)}</span>
                </p>
            {/if}
            {#if edit}
                <div class="flex items-end justify-between w-full gap-2">
                    <div data-checkout-action="change-qty" data-basket-item={item.id}>
                    <Select
                        options={options()}
                        prefix={t("Anzahl: ")}
                        bind:value={quantity}
                        onchange={() => (hasChanged = true)}
                    />
                    </div>
                    <button
                        type="button"
                        onclick={() => deleteItem(item.id)}
                        aria-label={t("Artikel entfernen")}
                        class="shrink-0 text-gray-103 hover:text-scarlet p-1 -mb-1 cursor-pointer transition-colors"
                        data-checkout-action="delete-basket-item"
                        data-basket-item={item.id}
                    >
                        <Icon name="trash" class="w-4 h-4"/>
                    </button>
                </div>
            {:else}
                <span class="font-light">{t("Menge:")} {quantity}</span>
            {/if}
        </div>
    </div>
</div>
